<template>
	<div
		:key="video.vidChron"
		:class="video.classes"
		:data-mode="mode"
		:data-chronicle="video.vidChron"
		:id="video.container"
		:data-dynvidid="config.dynVidId"
		data-e2e="dvid"
	>
		<div
			v-if="video.validChron"
			class="vue-video-wrapper empty"
		>
			<div class="sticky-header-bar">
				<div class="sticky-close-button">
					<img src="./assets/images/close.svg" />
				</div>
			</div>
			<UpNext
				v-if="config.cpOptions.flyout"
				:next-title="upNext.title"
				:next-thumb="upNext.thumb"
				:next-alt="upNext.alt"
			/>
			<button
				type="text"
				@click="config.modalScoreVisible = true"
				class="archive-info"
				v-if="config.isArchived"
			>
				This video is from the WebMD Archive.
			</button>
			<div
				class="modal-content"
				v-if="config.modalScoreVisible"
			>
				<span
					@click="config.modalScoreVisible = false"
					class="archive-close-icon webmd-icon-close"
				>
				</span>
				<h3>This video is from the WebMD Archive</h3>
				<p>
					WebMD archives video when it is identified as no longer
					medically accurate, to ensure our readers can easily
					find the most timely content.
				</p>
			</div>
			<div
				:id="video.id"
				:data-dynvidid="config.dynVidId"
				class="vue-video-player"
			>
			</div>
			<span
				v-if="config.isSkippable"
				v-html="adConfig.skipBtn"
			>
			</span>
			<SideInfobar
				:sub-heading="subHeading"
				:video-title="config.media.title"
			/>
			<div
				class="overlay-timer"
				v-if="showCountdownTimer && remainingTime"
			>
				<span>{{ timerOverlay }}</span>
			</div>
			<Infobar
				ref="infobar"
				:vid-container="video.container"
				:infobar-container="config.infobar.container"
				:infobar-title="config.infobar.title"
				:infobar-about="config.infobar.about"
				:infobar-transcript="config.infobar.transcript"
				:video-about="video.about"
				:video-reviewer="video.reviewer"
				:video-copyright="video.copyright"
				:video-sources="video.sources"
				:sponsor-content="!config.hideSponsorLabel && config.sponsorContent"
				:sponsor-label="config.sponsorLabel"
				:video-title="config.media.title"
				:video-transcript="config.transcriptText"
				:vid-chron="config.dynVidId ? config.dynVidId : video.vidChron"
				:is-user-paused="isUserPaused"
				:mode="mode"
				:page-name="pageData.pageName"
				:module-name="config.moduleName"
			/>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import oauth from 'webmd-oauth/src/index.es6';
import { ua } from 'webmd-useragent';
import debug from 'webmd-debug';
import { isEmpty, debounce } from 'lodash';
import TinyEmitter from 'tiny-emitter';
import { nextTick } from 'vue';
import useCmsStore from 'webmd-cms-store/src/store';
import { attachPlatformScript } from './js/helpers/attach-platformscript.es6';
import { videoApi, videoQueueApi, isNonProd } from './js/helpers/get-solrendpoint.es6';
import Infobar from './components/Infobar.vue';
import UpNext from './components/UpNext.vue';
import SideInfobar from './components/SideInfobar.vue';
import { adsManagerMixin, videoMetricsMixin, videoObserverMixin } from './mixins/index.es6';

/* global webmd */
export default {
	name: 'VideoPlayer',

	components: {
		UpNext,
		Infobar,
		SideInfobar,
	},

	data() {
		return {
			adConfig: {
				displayAdsSet: new Set(),
				googleTagFailed: false,
				iuParts: {
					mobile: {
						base: 'vid-cmbwwbmd',
						spon: 'vidsp-cmbwwbmd',
						sens: 'vidst-cmbwwbmd',
					},
					notMobile: {
						base: 'vid-conwebmd',
						spon: 'vidsp-conwbmd',
						sens: 'vidst-conwbmd',
					},
				},
				isVpaid: false,
				level3key: '',
				level4key: '',
				networkCode: '',
				skipBtn: '<div class="skip-btn timeout" role="button" aria-live="polite" tabindex="0">Skip Ad <span class="timer"></span></div>',
			},
			config: {
				ampPlayer: {},
				autoHideDelay: 1000,
				autoplay: false,
				autoplayPolicy: 'muted',
				checkRendition: false,
				continuousPlay: false,
				contextVideo: {
					articleSummary: '',
					publishedDate: '',
				},
				cpOptions: {
					flyout: false,
				},
				debug: false,
				disableInitAd: false,
				disablePlayer: false,
				disablePreroll: false,
				disableStickyPlayer: false,
				dynVidId: '',
				hideSponsorLabel: false,
				hls: {
					resources: [{
						/* eslint-disable no-template-curly-in-string */
						src: '${paths.libs}hls.min.js',
						defer: false,
						type: 'text/javascript',
					}],
					quality: {
						startLevel: 0,
					},
				},
				image_server_url: '',
				infobar: {
					about: true,
					container: true,
					title: true,
					transcript: true,
					transcriptText: '',
				},
				initialRender: true,
				inviewAutoplay: false,
				isArchived: false,
				isPageHidden: false,
				isSkippable: true,
				media: {
					title: '',
					poster: '',
					source: [
						{
							src: '',
							type: '',
						},
					],
					track: [
						{
							kind: 'captions',
							src: '',
							srclang: 'en',
							type: 'application/ttml+xml',
						},
					],
				},
				modalScoreVisible: false,
				moduleName: 'vidfloat',
				playlist: {
					enabled: false,
					mode: 'virtual',
				},
				playsinline: true,
				plugins: {
					ima: {
						resources: [
							{
								src: '//imasdk.googleapis.com/js/sdkloader/ima3.js',
								debug: '//imasdk.googleapis.com/js/sdkloader/ima3_debug.js',
								type: 'text/javascript',
								async: true,
							},
							{
								/* eslint-disable no-template-curly-in-string */
								src: '${paths.plugins}ima/Ima.min.js',
								debug: '${paths.plugins}ima/Ima.js',
								/* eslint-disable no-template-curly-in-string */
								type: 'text/javascript',
								async: true,
							},
						],
						adTagUrl: {
							params: {
								sz: '640x360',
								iu: '',
								ciu_szs: '',
								ad_rule: 1,
								description_url: 'www.webmd.com',
								impl: 's',
								gdfp_req: 1,
								env: 'vp',
								output: 'xml_vmap1',
								plcmt: 1,
								unviewed_position_start: 1,
								vpmute: 0,
								cust_params: {},
							},
						},
						autoPlayAdBreaks: true,
						disableCompanionAds: false,
						disableFlashAds: true,
						enabled: true,
						vpaidMode: 'enabled',
						companions: [],
					},
					react: {
						volume: {
							direction: 'vertical',
						},
						locales: {
							en: {
								AD_NEXT: 'ADVERTISEMENT: Your video will play in ',
							},
						},

						resources: [
							{
								src: 'https://unpkg.com/react@16.8.0/umd/react.production.min.js',
								type: 'text/javascript',
							},
							{
								src: 'https://unpkg.com/react-dom@16.8.0/umd/react-dom.production.min.js',
								type: 'text/javascript',
							},
							{
								src: 'https://cdnjs.cloudflare.com/ajax/libs/prop-types/15.8.1/prop-types.js',
								type: 'text/javascript',
							},
							{
								src: '#{paths.plugins}react/React.min.js',
								debug: '#{paths.plugins}react/React.js',
								type: 'text/javascript',
							},

							/*
							 * removing since we are using react.production.min.js
							 * as per recommendations from akamai team
							 */
							/*
							 * {
							 * src: '#{paths.plugins}react/libs/react.min.js',
							 * debug: '#{paths.plugins}react/libs/react.js',
							 * type: 'text/javascript',
							 * },
							 */

							// commenting react css to block fontawesome fonts from loading from akamai
							/*
							 * {
							 * src: "#{paths.plugins}react/React.min.css",
							 * debug: "#{paths.plugins}react/React.css",
							 * type: "text/css"
							 * },
							 */
						],

						hint: {
							enabled: false,
						},
					},
				},
				refreshAds: true,
				robustnessLevel: 'SW_SECURE_CRYPTO',
				settings: {
					defaults: {
						captions: {
							fontFamily: '',
							fontSize: '',
							windowColor: 'black',
							windowOpacity: '45%',
							visible: true,
						},
					},
				},
				sponsorContent: false,
				sponsorLabel: 'SPONSOR CONTENT',
				stickyPlayer: false,
			},
			isUserPaused: false,
			scrollDirection: 'down',
			lastScrollY: 0,
			modes: {
				apptprep: {
					config: {
						moduleName: 'vidrprem',
					},
				},
				article: {
					config: {
						autoplay: true,
						disableAdsInit: true,
						moduleName: 'vidfloat',
						stickyPlayer: true,
						continuousPlay: true,
						cpOptions: {
							max: 10,
							filter: 'topic',
							flyout: true,
							source: 'page',
						},
					},
				},
				fed: {
					config: {
						moduleName: 'vidrprem',
					},
				},
				interactive: {
					config: {
						moduleName: 'vidia',
						refreshAds: false,
						disablePreroll: true,
						muted: false,
					},
				},
				daily: {
					config: {
						moduleName: 'vidsite',
					},
				},
				branded: {
					config: {
						moduleName: 'vidrprem',
					},
				},
				symphony: {
					config: {
						moduleName: 'vidstry',
					},
				},
			},
			pageData: {},
			uaType: 'mobile',
			upNext: {
				chronId: '',
				title: '',
				thumb: '',
				alt: '',
			},
			video: {
				about: '',
				classes: 'module vue-video-container',
				container: '',
				id: '',
				title: '',
				topicId: '',
				url: '',
				validChron: false,
				vidChron: '',
				vidnum: 1,
			},
			ampScriptPromise: null,
			emitter: new TinyEmitter(),
			closePos: 'top-right',
			subHeading: 'Now Playing',
			isUserClickedMute: false,
			isUserClickedCC: false,
			remainingTime: 0,
			showCountdownTimer: false,
			noTranscriptAvailable: false,
		};
	},

	props: {
		chronicId: {
			type: String,
			default: '',
			required: true,
		},
		mode: {
			type: String,
			default: 'in-article',
		},
		options: {
			type: Object,
			default: () => ({}),
		},
	},

	mixins: [adsManagerMixin, videoMetricsMixin, videoObserverMixin],

	methods: {

		/**
		 * getQueryParam
		 * Checks url for the value of a query param
		 * @param {string} param - The query param to check
		 * @return {string} - The param value as string or null
		 */
		getQueryParam(param) {
			const { href } = window.location;
			const reg = new RegExp(`[?&]${param}=([^&#]*)`, 'i');
			const val = reg.exec(href);

			return val ? val[1] : null;
		},

		/**
		 * checkForPlaylist
		 * Checks for a video playlist component
		 * Enables communication between player and playlist
		 */
		checkForPlaylist() {
			const $vm = this;
			const playlistContainer = document.getElementsByClassName('video-playlist-container')[0];

			if (playlistContainer) {
				$vm.config.playlist.enabled = true;
				$vm.getVideoFromEvent();
			}
		},

		/**
		 * checkPrerollState
		 * Determines if preroll needs to be disabled
		 */
		checkPrerollState() {
			if (this.config.disablePreroll || (this.getQueryParam('preroll') === 'n')) {
				debug('[vueVideoPlayer] preroll disabled');
				this.config.disablePreroll = true;
				this.config.plugins.ima.enabled = false;
			}
		},

		/**
		 * Accepts Chronicle ID of video and makes call
		 * to SOLR endpoint to retrieve corresponding video data
		 * @param {string} chronicId - Chronicle ID of video
		 */
		getVideoFromChron(chronicId) {
			const $vm = this;
			const headers = oauth.generate({
				client_id: 'e4e3f73a-0ceb-4d37-939e-90ddb1238360',
				secret_key: '8720f366-3769-47e9-8156-187b9c6583b5',
			});
			const dataType = 'json';
			const apiUrl = videoApi();
			let solrData;

			debug('[vueVideoPlayer] getVideoFromChron via SOLR');
			axios
				.get(`${apiUrl}${chronicId}`, {
					headers,
					dataType,
				})
				.then((response) => {
					const imagePlaceholder = document.querySelectorAll('.preload-img-container');
					if (this.sTestValue && this.config.stickyPlayer !== true) {
						this.showCountdownTimer = true;
					}

					if (response.data !== '' && response.data !== null) {
						if (response.data === false || response.data.message === 'No response found') {
							debug(`[vueVideoPlayer] ${chronicId} not found in SOLR video endpoint`);
						} else if (response.data.code > 200) {
							debug('[vueVideoPlayer] SOLR issue:', response.data);
						} else if (response.data.webmd_rendition) {
							$vm.video.validChron = true;
							solrData = response.data.webmd_rendition.content.wbmd_asset;

							// Creating a new flag to check if the api has response while rebuilding the player.
							$vm.config.checkRendition = true;
							debug('[vueVideoPlayer] SOLR data:', solrData);

							if (imagePlaceholder) {
								imagePlaceholder.forEach((element) => {
									element.remove();
								});
							}

							$vm.setVideoData(solrData);
						}
					}

					if (!$vm.config.checkRendition) {
						$vm.config.disablePlayer = true;
						$vm.checkDisplayAdRules();
					}
				})
				.catch((error) => {
					debug(error);
					if (!$vm.config.checkRendition) {
						$vm.config.disablePlayer = true;
						$vm.checkDisplayAdRules();
					}
				});
		},

		/**
		 * Accepts json object returned from SOLR endpoint
		 * and maps values to our data and config object
		 * @param {object} videoData - json object returned from SOLR
		 */
		setVideoData(videoData) {
			const html = document.querySelector('html');
			const metadata = videoData.metadata_section || videoData.module_settings.metadata_section;
			const sponsored = metadata.wbmd_c_vid_sp;
			const archived = metadata.wbmd_archive_bool;
			this.remainingTime = Number(metadata.wbmd_c_duration.replace('s', '')) + 1;
			const imgResizerParam = '?resize=750px:*&output-quality=35';
			const publishedDate = metadata.wbmd_orig_pub_dt;
			let trnsPath;

			debug('[vueVideoPlayer] setVideoData');

			if (publishedDate) {
				this.config.contextVideo.publishedDate = this.formatPublishedDate(publishedDate);
			}

			this.getSummary(videoData);

			this.video.about = metadata.wbmd_desc_meta;
			// eslint-disable-next-line max-len

			if (
				metadata?.wbmd_cpyrt?.wbmd_copyright?.wbmd_copyright_statement
				|| metadata?.module_settings?.metadata_section?.wbmd_cpyrt
					?.wbmd_copyright?.wbmd_copyright_statement
			) {
				this.video.copyright = metadata.wbmd_cpyrt.wbmd_copyright.wbmd_copyright_statement
					|| metadata.module_settings.metadata_section.wbmd_cpyrt.wbmd_copyright
						.wbmd_copyright_statement;
			}

			this.video.sources = metadata.wbmd_c_vid_citations || '';
			this.video.topicId = metadata.wbmd_c_prim_top_id['@wbmd_storage_val'];
			this.config.media.title = metadata.title;
			// eslint-disable-next-line max-len
			if (metadata.wbmd_c_lg_vid_thmbnl && metadata.wbmd_c_lg_vid_thmbnl.image_path) {
				// eslint-disable-next-line max-len
				this.config.media.poster = this.config.image_server_url
					+ metadata.wbmd_c_lg_vid_thmbnl.image_path
					+ imgResizerParam;
			}
			this.config.media.source = this.formatVideoSource(metadata.wbmd_c_loc_url);

			const defaultCaption = 'https://img.lb.wbmdstatic.com/webmd_static_vue/file-explorer/webmd/video/xml/default-caption.xml';
			const defaultTranscript = 'There is no transcript available.';

			if (metadata.wbmd_c_vid_trns !== null) {
				const contentSection = videoData.content_section || videoData.module_data.content_section;
				const transObj = contentSection?.tt?.body?.div?.p;

				if (Array.isArray(transObj) === true) {
					trnsPath = metadata.wbmd_c_vid_trns['@path'] || metadata.wbmd_c_vid_trns.path;
					this.formatTranscript(videoData);

					if (this.config.media.track) {
						this.config.media.track[0].src = this.config.image_server_url + trnsPath;
					}
				} else {
					this.config.transcriptText = defaultTranscript;
					this.config.media.track[0].src = defaultCaption;
					this.noTranscriptAvailable = true;
				}
			} else {
				this.config.transcriptText = defaultTranscript;
				this.config.media.track[0].src = defaultCaption;
				this.noTranscriptAvailable = true;
			}

			if (metadata.wbmd_prim_med_revr !== null) {
				this.formatReviewed(videoData);
			}

			if (sponsored === '1') {
				this.config.sponsorContent = true;
				html.classList.add('sponsored');
			} else {
				this.config.sponsorContent = false;
				html.classList.remove('sponsored');
			}

			if (archived === '1') {
				this.config.isArchived = true;
				html.classList.add('archived');
			} else {
				this.config.isArchived = false;
			}

			/*
			 *  pass updateContextVideo event to daily video for coronavirus in context
			 * we are using this.$emit for communication between player and fed-video
			 */
			this.$emit('videoDataUpdated', this.config);

			// When all relevant configs are set, call checkDisplayAdRules
			this.checkDisplayAdRules();
		},

		formatTranscript(videoData) {
			const contentSection = videoData.content_section || videoData.module_data.content_section;
			const transObj = contentSection.tt.body.div.p;
			const transArr = Object.values(transObj);
			const rawTextArr = [];
			let rawText;
			let textCombined = '';
			let keyText;

			/* eslint-disable no-param-reassign */
			transArr.forEach((key) => {
				keyText = key['#text'] || key.text;

				if (keyText === undefined) {
					keyText = '';
				}

				// Add br tags if the transcript data have [br] key
				if (key.br !== undefined) {
					keyText = '<br><br>';
				}

				// rawText = key['#text'] || key['text'];
				rawText = keyText;

				// Get the index of 'colon with space' to construct the header or speaker name
				if (rawText !== undefined) {
					const idx = rawText.indexOf(': ');

					if (idx > 0) {
						rawText = rawText.replace(': ', ' ');
						rawText = `<div class="speaker-name">${rawText.slice(0, idx)}</div>${rawText.slice(idx)}`;
					}
				}

				rawTextArr.push(rawText);
			});
			/* eslint-disable no-param-reassign */

			textCombined = rawTextArr.join(' ').replace(/\s\s+/g, ' ');
			this.config.transcriptText = textCombined;
		},

		// format published Date
		formatPublishedDate(date) {
			const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
			const dateArr = date.split('/');
			const month = months[Number(dateArr[0]) - 1];
			const day = dateArr[1];
			const year = dateArr[2].substring(0, 4);
			const fullDate = `${month} ${day}, ${year}`;

			return fullDate;
		},

		// Get Video Summary and Summary Title
		getSummary(videoData) {
			const contentSection = videoData.content_section || videoData.module_data.content_section;
			const consVideo = contentSection.wbmd_cons_video;

			if (consVideo?.section_groups) {
				this.config.contextVideo.articleSummary = consVideo.section_groups['#cdata-section'];
			} else {
				// if the clicked video has no summary then update it as an empty string
				this.config.contextVideo.articleSummary = '';
			}
		},

		formatReviewed(videoData) {
			const metadata = videoData.metadata_section || videoData.module_settings.metadata_section;
			const primReviewer = metadata.wbmd_prim_med_revr || '';
			const firstname = primReviewer.wbmd_first_nm || '';
			const lastname = primReviewer.wbmd_lst_nm || '';
			const primReviewDate = metadata.wbmd_prim_revw_dt || '';
			const suffixTextArr = [];
			let reviewedDate = '';
			let reviewerText = '';
			const suffixGroup1 = primReviewer.wbmd_person_suffix_group || '';
			let suffixGroup;
			let suffix = '';
			let suffixText;

			reviewedDate = primReviewDate.split(' ').shift();

			/*
			 * if wbmd_person_suffix_group is null
			 * pass empty string
			 * otherwise pass full node
			 */
			if (suffixGroup1 === '') {
				suffixGroup = suffixGroup1;
			} else {
				suffixGroup = suffixGroup1.wbmd_person_suffix;
			}

			// if reviewer has suffix, format accordingly
			if (suffixGroup.length > 1) {
				suffixGroup.forEach((key) => {
					suffixText = key['@wbmd_disp_nm'];
					suffixTextArr.push(suffixText);
				});
				suffix = suffixTextArr.join(', ');
			} else {
				suffix = suffixGroup['@wbmd_disp_nm'] || '';
			}

			reviewerText = `${firstname} ${lastname}, ${suffix} ${reviewedDate}`;
			this.video.reviewer = reviewerText;
		},

		/**
		 * applyModeConfig
		 * Merge default config with chosen mode options
		 */
		applyModeConfig() {
			let modeConfig = {};
			const mode = this.mode === 'in-article' ? 'article' : this.mode;

			debug(`[vueVideoPlayer] mode: ${mode}`);

			switch (mode) {
				case 'daily':
				case 'fed':
					if (this.uaType === 'desktop' || window.innerWidth > 1024) {
						this.modes[mode].config.autoplay = true;
					}
					break;

				// Enable inview autoplay for 'apptprep' mode only on desktop or large screens
				case 'apptprep':
					if (this.uaType === 'desktop' || window.innerWidth >= 1024) {
						this.config.inviewAutoplay = true;
					}
					break;
				default:
			}
			modeConfig = this.modes[mode].config;
			debug('[vueVideoPlayer] Apply Mode Config:', modeConfig);
			this.config = Object.assign(this.config, modeConfig);
		},

		/**
		 * mergeOptions
		 * Merge default config with options
		 */
		mergeOptions() {
			// Merge window.videoOptions with this.options if not empty
			if (!isEmpty(window.videoOptions)) {
				this.options = { ...this.options, ...window.videoOptions };
			}

			// Check if options exist
			if (!isEmpty(this.options)) {
				/**
				 * Extract fields outside Breakpoints in options object -
				 * only Mobile & Desktop Breakpoints allowed
				 */
				const {
					mobile, desktop, infobar, ...fieldsOutsideBreakpoints
				} = this.options;

				// Merge infobar options with config.infobar directly if infobar option exists
				if (infobar) {
					this.config.infobar = { ...this.config.infobar, ...infobar };
					debug('[vueVideoPlayer] Fields Outside Breakpoints infobar options:', infobar);
				}

				// Check if fields Outside Breakpoints exist
				if (fieldsOutsideBreakpoints) {
					this.config = { ...this.config, ...fieldsOutsideBreakpoints };
					debug('[vueVideoPlayer] Fields Outside Breakpoints options:', fieldsOutsideBreakpoints);
				}

				const breakpointOptions = this.options[this.uaType];

				// Check if breakpoint-based options exist for the current breakpoint
				if (breakpointOptions) {
					const { infobar: breakpointInfobar, ...otherBreakpointOptions } = breakpointOptions;

					/**
					 * Merge infobar options with config.infobar directly
					 * if infobar exists in breakpoint options
					 */
					if (breakpointInfobar) {
						this.config.infobar = { ...this.config.infobar, ...breakpointInfobar };
						debug('[vueVideoPlayer] Breakpoint Infobar options:', breakpointInfobar);
					}

					// Merge breakpointOptions into config
					if (!isEmpty(otherBreakpointOptions)) {
						this.config = { ...this.config, ...otherBreakpointOptions };
						debug('[vueVideoPlayer] Breakpoints options:', otherBreakpointOptions);
					}
				}
			}

			// Set dynamic video ID if inviewAutoplay is enabled and no dynVidId is provided
			if (this.config.inviewAutoplay) {
				this.config.autoplay = false;
				if (!this.config.dynVidId) {
					this.config.dynVidId = this.chronicId;
				}
			}
		},

		// check conditions to determine whether to disable initial ads
		checkDisableInitialAd() {
			/**
			 * Iff Disable Preroll is False(i.e Preroll is enabled) and
			 * the Video is autoplay or the disableInitAd is True
			 */
			if (!this.config.disablePreroll && (this.config.disableInitAd || this.config.autoplay)) {
				/**
				 * disable initial load of display ads
				 * so we can setup preroll and update video data
				 */
				this.config.disableInitAd = true;
				try {
					debug('[vueVideoPlayer] Disable Initial Ad: True');

					/* eslint-disable no-undef */
					webmd.ads.disableInitialLoad();

					/*
					 * set disableInitialLoad flag on ads object
					 * temp to support MedRef release
					 * will go into global ads framework later
					 */
					webmd.ads.disableAdsInit = true;

					/* eslint-disable no-undef */
				} catch (each) {
					debug(e);
				}
			} else {
				debug('[vueVideoPlayer] Disable Initial Ad: False');
			}
		},

		/**
		 * Handles pagination/hash changes (used by in-article mode)
		 * - Hides video player (and pauses video) if user navigates from pg1
		 * - Shows paused video player if user comes back to pg 1
		 */
		handleHashChange() {
			const $vm = this;
			const videoContainer = document.getElementById(this.video.container);

			window.addEventListener('hashchange', () => {
				const hashString = window.location.hash;
				if (/^\d+$/.test(hashString.substring(1)) && hashString.length < 3) {
					if (hashString !== '#1') {
						videoContainer.parentElement.style.display = 'none';
						$vm.ampPlayer.pause();
					} else {
						videoContainer.parentElement.style.display = 'block';
						$vm.ampPlayer.play();
					}
				}
			});
		},

		/**
		 * positionInfoBar
		 * Moves info bar container into video player
		 * @todo May need to tweak position
		 */
		positionInfoBar() {
			const videoPlayerEl = document.getElementById(this.video.id);
			const infobar = document.getElementById(this.video.container).getElementsByClassName('infobar-container')[0] || null;

			// make sure infobar hasn't already been moved
			if (infobar) {
				infobar.remove();

				// setTimeout(() => {
				videoPlayerEl.append(infobar);

				// Remove infobar for smaller videos in interactive mode
				if (this.mode === 'interactive' && videoPlayerEl.offsetWidth < 251) {
					infobar.remove();
				}

				// }, 500);
			}
		},

		/**
		 * formatVideoSource
		 * Formats raw video source into array for Akamai player
		 * @param {string} videoSrc - value from 'wbmd_c_loc_url' video object
		 * Supports:
		 * HLS: application/x-mpegURL
		 * MP4: video/mp4
		 * Does not suport:
		 * HDS: video/f4m
		 * DASH: application/dash+xml
		 * RTMP: video/f4m
		 */
		formatVideoSource(videoSrc) {
			const sourceArr = [];

			// const delimiter = '_,';

			/*
			 * PPE-533140: Commenting out to test HLS streaming
			 * // edit array to play mp4 files directly
			 * if (videoSrc.includes(delimiter)) {
			 * 	eslint-disable-next-line no-param-reassign
			 * 	videoSrc = `${videoSrc.split(delimiter)[0]}_750k.mp4`;
			 * }
			 *
			 * sourceArr.push({
			 * 	src: `//webmd-a.akamaihd.net/delivery${videoSrc}`,
			 * 	type: 'video/mp4',
			 * });
			 */

			// pass all resolutions to play HLS streaming video
			sourceArr.push({
				src: `//webmd-vh.akamaihd.net/i/delivery${videoSrc}.csmil/master.m3u8`,
				type: 'application/x-mpegURL',
			});

			return sourceArr;
		},

		/**
		 * createVideoPlayer
		 * Creates video player using Akamai's Adaptive Media Player
		 * @returns {object} Returns a Promise object containing player's elements and events
		 */
		createVideoPlayer() {
			this.ampScriptPromise.then(() => {
				if (
					this.video.validChron
					&& window?.akamai
					&& this.config.checkRendition
				) {
					// @TODO: PPE-347449: remove sponsor value

					debug('[vueVideoPlayer] createVideoPlayer w/config', this.config);

					nextTick(() => {
						window.akamai.amp.AMP.create(this.video.id, this.config, (event) => {
							// pass events to ampPlayerReady
							this.ampPlayerReady(event);
							this.config.checkRendition = false;
						});
					});
				}
			});
		},

		/**
		 * ampPlayerReady
		 * Uses AMP's ready event object to inspect player events and pass data to other methods
		 * @param {event} event - Returned Promise object from AMP.create
		 */
		ampPlayerReady(event) {
			const videoContainer = document.getElementById(this.video.container);
			const videowrapper = videoContainer.getElementsByClassName('vue-video-wrapper')[0];
			const apptPrepPage = document.querySelector('.appt-prep-intent');

			// address min-height browser issue
			videoContainer.style.minHeight = '0';

			/**
			 * Adjusts the video container style to remove the minimum height constraint.
			 */
			if (apptPrepPage) {
				const apptPrepVideo = apptPrepPage.querySelector('.video-packaging');

				if (apptPrepVideo) {
					apptPrepVideo.style.minHeight = '0';
				}
			}

			// Fire event to when Amp player is ready
			this.emitter.emit('vp-ready');
			debug('[vueVideoPlayer] Player is Ready');

			// assign instance to component obj
			this.ampPlayer = event.player;

			// setting up reference or this will fail
			window.vueVideo = window.vueVideo || {};

			// if dynamic id exists assign it to instance, otherwise use chron id
			if (this.config.dynVidId) {
				/* eslint-disable no-underscore-dangle */
				window.vueVideo[this.config.dynVidId] = this.ampPlayer._player;
			} else {
				/* eslint-disable no-underscore-dangle */
				window.vueVideo[this.video.vidChron] = this.ampPlayer._player;
			}

			debug('[vueVideoPlayer] ampPlayer', this.ampPlayer);

			// add mode class name to container
			videoContainer.classList.add(this.mode);

			// track hashChange event for in-article mode
			if (this.mode === 'in-article') {
				window.onhashchange = this.handleHashChange();
			}

			if (this.config.stickyPlayer === true) {
				this.makePlayerSticky(event);
			}

			// Remove placeholder and disable info & control bar
			videowrapper.classList.remove('empty');

			// assemble Control Bar and create Custom Events
			this.assembleControlBar();
			this.createCustomEvents();

			const nowPlayingEvt = new CustomEvent('evt-video-nowplaying', {
				detail: this.video.vidChron,
			});
			document.dispatchEvent(nowPlayingEvt);
		},

		/**
		 * assembleControlBar
		 * Moves control bar elements into desired position
		 */
		assembleControlBar() {
			const videoContainer = document.getElementById(this.video.container);
			const dur = videoContainer.getElementsByClassName('amp-duration')[0];
			const full = videoContainer.getElementsByClassName('amp-fullscreen')[0];
			const mute = videoContainer.getElementsByClassName('amp-mute')[0];
			const vol = videoContainer.getElementsByClassName('amp-volume')[0];
			const progress = videoContainer.getElementsByClassName('amp-progress')[0];

			// volume button does not render on mobile
			if (vol) {
				full.before(mute, vol);
			}

			dur.before(progress);
		},

		/**
		 * createCustomEvents
		 * A method for wrapping AMP events to create our own events
		 */
		createCustomEvents() {
			const $vm = this;
			const player = this.ampPlayer;
			let timeoutId = null;
			const videoTitle = this.config.media.title;
			const fullscreenButton = document.getElementById('fullscreen-button');
			const videoContainer = document.getElementById(this.video.container);
			const videowrapper = videoContainer.getElementsByClassName('vue-video-wrapper')[0];
			const videoPlayer = videoContainer.getElementsByClassName('vue-video-player')[0];

			fullscreenButton.addEventListener('click', () => {
				if (player.displayState === 'normal') {
					player.displayState = 'full-screen';
					videowrapper.classList.remove('mobile-sticky');
				}
			}, { passive: true });

			player.addEventListener('fullscreenchange', (event) => {
				if (event.data === false
					&& videowrapper.classList.contains('sticky')
					&& window.innerWidth < 768) {
					videowrapper.classList.add('mobile-sticky');
				}
			}, { passive: true });

			['playing', 'paused', 'mutechange'].forEach((e) => {
				player.addEventListener(e, () => {
					if (!videoPlayer.classList.contains('amp-ad-break')) {
						if (timeoutId !== null) {
							clearTimeout(timeoutId);
						}
						videoPlayer.classList.remove('hide-button');
						timeoutId = setTimeout(() => {
							videoPlayer.classList.add('hide-button');
						}, 2000);
					}
				}, { passive: true });
			});

			/* eslint-disable no-unused-vars */
			/* eslint-disable no-shadow */
			player.addEventListener('adloaded', (player) => {
				$vm.showCountdownTimer = false;
				this.emitter.emit('vp-adloaded', player);
				debug('[vueVideoPlayer] vp-adloaded', player.data);
				$vm.subHeading = 'Up Next';
			}, { passive: true });

			player.addEventListener('aderror', (player) => {
				if ($vm.sTestValue && $vm.config.stickyPlayer !== true) {
					$vm.showCountdownTimer = true;
				}
				this.emitter.emit('vp-aderror', player);
				$vm.subHeading = 'Now Playing';
				debug('[vueVideoPlayer] vp-aderror', player.data);
			}, { passive: true });

			player.addEventListener('adbreakstart', (player) => {
				this.emitter.emit('vp-adbreakstart', player);
				debug('[vueVideoPlayer] vp-adbreakstart', player.data);
				$vm.addAdTypeClass(player);
			}, { passive: true });

			player.addEventListener('adplaying', (player) => {
				this.emitter.emit('vp-adplaying', player);
				debug('[vueVideoPlayer] vp-adplaying', player);
				$vm.runskipAd();
			}, { passive: true });

			player.addEventListener('adended', (player) => {
				if ($vm.sTestValue && $vm.config.stickyPlayer !== true) {
					$vm.showCountdownTimer = true;
				}
				this.emitter.emit('vp-adended', player);
				debug('[vueVideoPlayer] vp-adended', player);
				$vm.subHeading = 'Now Playing';
				$vm.removeAdTypeClass();

				// $vm.enableInfoControl();
			}, { passive: true });

			player.addEventListener('adskipped', () => {
				this.emitter.emit('vp-adskipped', player);
				debug('[vueVideoPlayer] vp-adskipped', player);
				$vm.subHeading = 'Now Playing';
				$vm.removeAdTypeClass();

				// $vm.enableInfoControl();
			}, { passive: true });

			player.addEventListener('initialized', (player) => {
				this.emitter.emit('vp-initialized', player);
				debug('[vueVideoPlayer] initialized', player);
			}, { passive: true });

			// we are using this.$emit for communication between player and fed-video
			player.addEventListener('playing', (player) => {
				this.$emit('vp-playing', player);
				debug('[vueVideoPlayer] Playing:', player.player.config.media.title);
			}, { passive: true });

			// we are using this.$emit for communication between player and fed-video
			player.addEventListener('paused', (player) => {
				this.$emit('vp-paused', player);
				debug('[vueVideoPlayer] Paused', player);
			}, { passive: true });

			player.addEventListener('resume', (player) => {
				this.emitter.emit('vp-resume', player);
				debug('[vueVideoPlayer] Resumed', player);
			}, { passive: true });

			player.addEventListener('mutechange', (player) => {
				if (this.isUserClickedMute) {
					if (player.data) {
						this.emitter.emit('vp-muted');
					} else {
						this.emitter.emit('vp-unmuted');
					}
				}
				this.isUserClickedMute = false;
			}, { passive: true });

			player.addEventListener('captioningvisibilitychange', (player) => {
				if (this.isUserClickedCC) {
					if (player.data) {
						this.emitter.emit('vp-ccenabled');
					} else {
						this.emitter.emit('vp-ccdisabled');
					}
				}
				this.isUserClickedCC = false;
			}, { passive: true });

			player.addEventListener('mediasequencestarted', (player) => {
				this.emitter.emit('vp-mediasequencestarted', player);
				debug('[vueVideoPlayer] mediasequencestarted', player);
			}, { passive: true });

			player.addEventListener('mediasequenceended', (player) => {
				this.emitter.emit('vp-after', player);

				const videoEndedEvt = new CustomEvent('evt-video-ended', {
					detail: this.video.vidChron,
				});
				document.dispatchEvent(videoEndedEvt);
			}, { passive: true });

			/* eslint-disable max-len */
			/*
			 *  Tracks the playstate of the following:
			 * 'ready': indicates that AMP’s application level playback state has changed to a state of readiness. This occurs at different junctures between initialization and continuous playback scenarios
			 * 'loading': indicates that loading is underway. In the case of HTML5 based playback, this is after the loadstart event has fired, and progress events have completed
			 * 'playing': indicates that the media is currently playing
			 * 'paused': indicates that media is currently paused
			 * 'seeking': indicates that a seek is in progress
			 * 'waiting': indicates that video has stopped so that the next frame can be buffered
			 * 'ended': indicates that media has completed
			 * 'error': indicates that an error occurred in relation to media playback
			 */
			/* eslint-disable max-len */
			player.addEventListener('playstatechange', (player) => {
				this.emitter.emit('vp-playstate', player);
			}, { passive: true });

			player.addEventListener('activestatechange', (player) => {
				this.emitter.emit('vp-activestate', player);
				debug('[vueVideoPlayer] Active', player);
			}, { passive: true });

			player.addEventListener('timeupdate', (player) => {
				this.handleDurationChange(player);
			}, { passive: true });

			this.emitter.on('vp-25pct', () => {
				debug('[vueVideoPlayer] Completed 25%', videoTitle);

				/*
				 * For Time Tracking on Fed Video Assets
				 * we want the fed-video to listen to 'vp-25pct' only once
				 */
				if ($vm.mode === 'fed') {
					this.$emit('vp-25pct', player);
				}
				this.callAdhoc('25pct', '');
			}, { passive: true });

			this.emitter.on('vp-50pct', () => {
				debug('[vueVideoPlayer] Completed 50%', videoTitle);
				this.callAdhoc('50pct', '');
			}, { passive: true });

			this.emitter.on('vp-75pct', () => {
				debug('[vueVideoPlayer] Completed 75%', videoTitle);
				this.callAdhoc('75pct', '');
			}, { passive: true });

			player.addEventListener('started', (player) => {
				// Fire start metrics when the video plays
				this.emitter.emit('vp-started', player);
				debug('[vueVideoPlayer] Started', player);
				this.callAdhoc('start', '');
			}, { passive: true });

			player.addEventListener('ended', (player) => {
				// eslint-disable-next-line no-underscore-dangle
				Promise.resolve(window.__loada9Promise).then(() => {
					$vm.getA9Bids();
				});
				this.emitter.emit('vp-ended', player);
				debug('[vueVideoPlayer] Video Ended', player);
				this.callAdhoc('100pct', '');
			}, { passive: true });

			player.addEventListener('fullscreenchange', (player) => {
				if (player.data) {
					this.emitter.emit('vp-fullscreen');
				} else {
					this.emitter.emit('vp-normalscreen');
				}
			}, { passive: true });

			player.addEventListener('volumechange', (player) => {
				this.emitter.emit('video-play-volume-change', player);
			}, { passive: true });

			player.addEventListener('error', (player) => {
				const errorData = player?.data?.metadata;

				/*
				 * the if block below checks if the error from akamai is a manifestLoadError
				 * and if the url contains master.m3u8
				 * this will tell us if the streaming file on the player is failing
				 * and PCE team can get this issue fixed for that URL by reaching out to Akamai
				 */
				if (errorData?.details === 'manifestLoadError' && errorData?.url.includes('master.m3u8')) {
					debug('[vueVideoPlayer] Network Error - master.m3u8 link failed');
					this.createBeaconCall('network-error', 'streamingFailed', 'failed-streaming-link');
				}
			}, { passive: true });

			/**
			 * Custom visibility change event
			 * This event updates isPageHidden flag to determine
			 * if the window/tab is in focus or not
			 */
			document.addEventListener('visibilitychange', () => {
				if (document.visibilityState === 'hidden') {
					this.config.isPageHidden = true;
				} else {
					this.config.isPageHidden = false;
				}
			});
		},

		/**
		 * vueCustomEvents
		 * A method for wrapping Vue instance events
		 */
		vueCustomEvents() {
			/**
			 * Custom gpt ad not found event
			 * This event determines
			 * if the preroll is not present
			 */
			if (!this.config.disablePreroll || this.mode !== 'interactive') {
				document.addEventListener('wbmd:ads_googletag_failed', (e) => {
					this.createBeaconCall('gpt-error', 'adsFailed', 'failed-google-ads');
					debug('[vueVideoPlayer] GPT Not Found');
					if (!this.config.disablePlayer) {
						this.config.disablePreroll = true;
						this.adConfig.googleTagFailed = true;
						this.config.plugins.ima.enabled = false;
						this.createVideoPlayer();
					}
				});
			}

			// Run these functions when video player is ready
			this.emitter.on('vp-ready', () => {
				this.bindClickMetrics();
				this.positionInfoBar();
				this.showUnmuteOverlay();

				// Observe Videos for Tracking Mode Impression and Trigger Inview autoplay Functionality, if enabled
				this.observeVideos();
				debug(`[vueVideoPlayer] ${this.video.container} player is vp-ready`);
			});

			// closed caption icon is delayed so check when video starts
			this.emitter.on('vp-started', () => {
			});

			/*
			 * refresh display ads when media starts playing
			 */
			this.emitter.on('vp-mediasequencestarted', () => {
				setTimeout(() => {
					debug('[vueVideoPlayer] video started');

					if ((!this.config.disablePreroll || this.adConfig.googleTagFailed)
					&& this.config.refreshAds) {
						this.convertToCompanions();
					}
				}, 2000);
			});

			// remove the skip button if not skipped and ad ended.
			this.emitter.on('vp-adended', () => {
				const videoContainer = document.getElementById(this.video.container);
				const skipBtn = videoContainer.getElementsByClassName('skip-btn')[0];

				if (skipBtn) {
					skipBtn.remove();
				}
			});

			// create Up Next flyout
			this.emitter.on('vp-vqready', () => {
				if (window.videoQueue.length > 0 && this.config.continuousPlay) {
					this.createUpNextFlyout();
				}
			});

			/*
			 * if continuous play and this is not the last video
			 * in the queue run continuous play
			 */

			this.emitter.on('vp-after', () => {
				if (this.config.continuousPlay) {
					if ((this.config.continuousPlay !== window.videoQueue.length + 1) && !this.config.isPageHidden) {
						debug('[vueVideoPlayer] runContinousPlay');

						/*
						 * Fetches videos based on the given topic ID or channel and queues the videos
						 * plays the next video in the sequence
						 */
						const videos = window.videoQueue;
						this.rebuildPlayer(videos[this.config.continuousPlay - 1].chronId);
					} else {
						debug('[vueVideoPlayer] CP discontinued');
						this.callAdhoc('inctv', '');
					}
				}
			});

			this.emitter.on('vp-ccenabled', () => {
				this.callAdhoc('ccy', '-ctl');
				debug('[vueVideoPlayer] Closed Caption: Enabled');
			});

			this.emitter.on('vp-ccdisabled', () => {
				this.callAdhoc('ccn', '-ctl');
				debug('[vueVideoPlayer] Closed Caption: Disabled');
			});

			this.emitter.on('vp-fullscreen', () => {
				this.callAdhoc('bigr', '-ctl');
				debug('[vueVideoPlayer] Screen Change: Full');
			});

			this.emitter.on('vp-normalscreen', () => {
				this.callAdhoc('smlr', '-ctl');
				debug('[vueVideoPlayer] Screen Change: Normal');
			});

			this.emitter.on('vp-muted', () => {
				if (this.config.inviewAutoplay) {
					window.dynVideoState[this.config.dynVidId].audioState = 'muted';
				}
				this.callAdhoc('mute', '-ctl');
				this.config.muted = true;
				this.config.autoplayPolicy = 'muted';
				debug('[vueVideoPlayer] Video Muted');
			});

			this.emitter.on('vp-unmuted', () => {
				if (this.config.inviewAutoplay) {
					window.dynVideoState[this.config.dynVidId].audioState = 'unmuted';
				}
				this.callAdhoc('unmute', '-ctl');
				this.config.muted = false;
				this.config.autoplayPolicy = 'none';
				debug('[vueVideoPlayer] Video Unmuted');
			});
		},

		/**
		 * addAdTypeClass
		 * Add Ad Type VAST or VPAID to video container ad events when the ad plays
		 */
		addAdTypeClass(player) {
			const $vm = this;
			const videoContainer = document.getElementById(this.video.container);
			const info = document.querySelector('.amp-ad-time');
			const label = document.createElement('div');

			if (player.data.metadata && player.data.metadata.g) {
				// set VPAID boolean
				$vm.adConfig.isVpaid = player.data.metadata.g.vpaid;

				label.innerText = this.ampPlayer.getLocalizedString('AD_NEXT');
				info.before(label);

				if (this.adConfig.isVpaid === true) {
					videoContainer.classList.add('ad-vpaid');
					debug('[vueVideoPlayer] ad-vpaid');
				} else {
					videoContainer.classList.add('ad-vast');
					debug('[vueVideoPlayer] ad-vast');
				}
			}
		},

		/**
		 * removeAdTypeClass
		 * Remove Ad Type VAST or VPAID to video container ad events when the ad ends
		 */
		removeAdTypeClass() {
			const videoContainer = document.getElementById(this.video.container);
			videoContainer.classList.remove('ad-vpaid', 'ad-vast');
		},

		/**
		 * checkDisplayAdRules
		 * Checks settings/business rules to determine
		 * if display ads should be refreshed
		 */
		checkDisplayAdRules() {
			const $vm = this;

			debug('[vueVideoPlayer] checkDisplayAdRules');

			if ($vm.getQueryParam('refresh') === 'n') {
				$vm.config.refreshAds = false;
			}

			// eslint-disable-next-line no-underscore-dangle
			Promise.resolve(window.__loada9Promise).then(() => {
				$vm.getA9Bids();
			});

			// if preroll and player are not disabled
			if (!$vm.config.disablePreroll && !$vm.config.disablePlayer) {
				window.webmd.ads.googleDefer(() => {
					this.config.plugins.ima.enabled = true;
					$vm.setCustomParams();
					$vm.initObserver(webmd?.ads?.ads);
					$vm.createInitialPlayer();
				});
			} else {
				if (this.config.disablePreroll && this.config.refreshAds && this.config.autoplay) {
					debug('[vueVideoPlayer] preroll disabled, refreshing display ads');
					webmd.ads.refresh();
				}
				if (!this.config.disablePlayer) {
					$vm.createInitialPlayer();
				}
			}
		},

		/**
		 * runskipAd
		 * Checks for window object delivered by the ad server via companion ad slot
		 * If found, calls function to create skip ad button
		 * If video companion ads are disabled, this functionality will not work
		 */
		runskipAd() {
			debug('[vueVideoPlayer] runskipAd checking...');

			// Uses a setTimeout to wait for ad creative to return
			setTimeout(() => {
				if (window?.webmd?.m?.videoUiApi?.videoAd?.skippable) {
					this.config.isSkippable = true;

					debug('[vueVideoPlayer] runskipAd code found');
					this.createSkipButton();
				} else {
					debug('[vueVideoPlayer] runskipAd code not found');
				}
			}, 2000);
		},

		/**
		 * createSkipButton
		 * Add skip ad button for manual skipping of a preroll.
		 * Uses a window object delivered by the ad server via companion ad slot
		 * If video companion ads are disabled, this functionality will not work
		 */
		createSkipButton() {
			const $vm = this;
			const videoContainer = document.getElementById(this.video.container);
			const skipBtn = videoContainer.getElementsByClassName('skip-btn')[0];
			const timer = skipBtn.getElementsByClassName('timer')[0] || null;

			const timeout = (window.webmd.m.videoUiApi.videoAd && window.webmd.m.videoUiApi.videoAd.timeout) || 5;
			const pos = (window.webmd.m.videoUiApi.videoAd && window.webmd.m.videoUiApi.videoAd.position) || 'bottom-right';
			let timerEle;
			/* eslint-disable max-len */

			debug('[vueVideoPlayer] createSkipButton', pos);

			if (skipBtn) {
				skipBtn.classList.add(pos);
				timer.innerHTML = timeout;

				// create countdown
				const skipTimer = setInterval(() => {
					timerEle = timer.innerHTML;

					/*
					 * When the timer gets down to 0,
					 * clear the interval and add click handler
					 * on click, remove the skip button
					 */
					if (timerEle > 1) {
						timer.innerHTML = timerEle - 1;
					} else {
						clearInterval(skipTimer);

						skipBtn.classList.remove('timeout');

						skipBtn.addEventListener('click', () => {
							$vm.callAdhoc('skipad', '-ctl');
							if ($vm.sTestValue && $vm.config.stickyPlayer !== true) {
								$vm.showCountdownTimer = true;
							}
							$vm.ampPlayer.ads.terminateAd();
							$vm.subHeading = 'Now Playing';
							skipBtn.remove();

							// $vm.enableInfoControl();
							return false;
						});
					}
				}, 1000);
			}
		},

		/**
		 * Fires a beacon call to Kibana for tracking
		 * @param {string} logType - type of log ('setup error', 'playback error', 'preroll error')
		 * @param {string} evt - method in which error was triggered
		 * @param {string} sid - error message
		 */
		createBeaconCall(logType, evt, sid) {
			// do not run beacon call for non Production enviroments like localhost
			if (!isNonProd && this.getQueryParam('beacon') !== 'n' && !this.config.isArchived) {
				window.webMDBeaconCalls.push({
					type: `video-log-${logType}`,
					event: `${evt}`,
					sid,
					wcbEvent: `${this.mode}`,
					url: document.URL,
				});
			}
		},

		/**
		 * pageIsSensitive
		 * Checks window object for s_sensitive value
		 * @returns {boolean}
		 */
		pageIsSensitive() {
			return !!(window.s_sensitive && window.s_sensitive === 'true');
		},

		/**
		 * pageIsSponsored
		 * Checks window object for s_sponsor_program and s_sponsor_brand values
		 * @returns {boolean}
		 */
		pageIsSponsored() {
			// eslint-disable-next-line max-len
			return !!((window.s_sponsor_program && window.s_sponsor_program.length > 0) || (window.s_sponsor_brand && window.s_sponsor_brand.length > 0));
		},

		/**
		 * updateIuPartsPreroll
		 * conditionally:
		 *   sets iu parts for preroll ad call
		 *   updates third and fourth level page targets for display ads
		 */
		updateIuPartsPreroll() {
			const { iuParts } = this.adConfig;
			const imaPlugin = this.config.plugins.ima;
			const adTagIUParts = ['consumer', 'webmd'];
			let videoIUParts = '';

			// if mobile device update iu_parts
			if (this.uaType === 'mobile') {
				adTagIUParts[0] = 'consmobileweb';

				switch (adTagIUParts[1]) {
					case 'webmd':
						adTagIUParts[1] = 'webmdmobileweb';
						break;
					default:

					// no default needed
				}
			}

			// set network code
			this.getAdNetworkCode();

			// if in-article modes, override default IU parts
			if (this.mode === 'in-article') {
				iuParts.mobile.base = 'vid-cmbwwbmd-emb';
				iuParts.mobile.sens = 'vidst-cmbwwbmd';
				iuParts.mobile.spon = 'vidsp-cmbwwbmd';
				iuParts.notMobile.base = 'vid-conwbmd-emb';
				iuParts.notMobile.sens = 'vidst-conwbmd-emb';
				iuParts.notMobile.spon = 'vidsp-conwbmd-emb';
			}

			debug('[vueVideoPlayer] pageIsSponsored', this.pageIsSponsored());

			/*
			 * set levl 3 and level 4 keys based on
			 * check for mobile, sponsored, and sensitive topic
			 */
			if (this.uaType === 'mobile') {
				this.adConfig.level3key = iuParts.mobile.base;
				if (this.pageIsSponsored()) {
					this.adConfig.level4key = iuParts.mobile.spon;
				} else if (this.pageIsSensitive()) {
					this.adConfig.level4key = iuParts.mobile.sens;
				}
			} else {
				this.adConfig.level3key = iuParts.notMobile.base;
				if (this.pageIsSponsored()) {
					this.adConfig.level4key = iuParts.notMobile.spon;
				} else if (this.pageIsSensitive()) {
					this.adConfig.level4key = iuParts.notMobile.sens;
				}
			}

			/*
			 * if preroll is not disabled,
			 * add network code to cloned orig adTarget array,
			 * and convert into iu parts string for IMA
			 */
			if (!this.config.disablePreroll) {
				adTagIUParts.unshift(this.adConfig.networkCode);

				if (!adTagIUParts.includes(this.adConfig.level3key)) {
					adTagIUParts.push(this.adConfig.level3key);
				}

				if (this.adConfig.level4key && !adTagIUParts.includes(this.adConfig.level4key)) {
					adTagIUParts.push(this.adConfig.level4key);
				}

				videoIUParts = adTagIUParts.filter(Boolean).join('/');
				imaPlugin.adTagUrl.params.iu = `/${videoIUParts}`;

				debug('[vueVideoPlayer] iu_parts for preroll', adTagIUParts);
			}
		},

		/**
		 * setCustomParams
		 * sets cust_params data needed for video preroll ad call
		 */
		setCustomParams() {
			const adArr = [];
			const compArr = [];
			const posArr = [];
			const posSizes = [];
			let autplyVal = 1;
			let combinedParams = {};
			let videoParams = {};
			const imaPlugin = this.config.plugins.ima;
			const imaParams = imaPlugin.adTagUrl.params;
			const { pageData } = this;

			debug('[vueVideoPlayer] setCustomParams');

			// update if not autoplay
			if (!this.config.autoplay) {
				autplyVal = 0;
			}

			// base object for video params
			videoParams = {
				app: 37,
				art: pageData?.pageId,
				autply: autplyVal,
				leaf: pageData?.leaf,
				plyr: 'html',
				pos: '',
				pt: pageData?.topic,
				sname: 'core',
				uri: pageData?.uri,
				vidnum: this.video.vidnum,
				vpt: this.video.topicId,
				xsl: '091e9c5e8088b964',
			};

			// push valid ad objects into ad array
			Object.entries(webmd.ads.ads).forEach(([key, value]) => {
				if (value.id !== 'undefined') {
					adArr.push(value);
				}
			});

			adArr.forEach((key) => {
				// get pos values
				if (key.pos !== undefined) {
					posArr.push(key.pos);
				}

				/*
				 * get slot id and first array of sizes
				 * to create companion slot objs
				 * This code is intended to track global ads on the initial page load
				 * and includes only those ads whose IDs start with 'ad-'
				 */
				if (key.id !== undefined && key.id.startsWith('ad-')) {
					const compObj = Object.create({});
					compObj.id = key.id;
					compObj.height = key.sizes[0][1] || key.sizes[1];
					compObj.width = key.sizes[0][0] || key.sizes[0];
					posSizes.push(`${compObj.width}x${compObj.height}`);
					compArr.push(compObj);
				}
			});

			// assign companions to AdTagURL
			imaPlugin.companions = compArr;

			// assign ciu_szs to AdTagURL
			imaParams.ciu_szs = posSizes.toString();

			// assign plcmt to AdTagURL
			if (this.mode === 'branded' || this.mode === 'symphony' || this.mode === 'apptprep') {
				imaParams.plcmt = 2;
			}

			// assign vpmute to AdTagURL
			if (this.config.autoplay || this.config.inviewAutoplay) {
				imaParams.vpmute = 1;
			}

			// assign webmd.ads.pageTargets.usps to AdTagURL
			if (webmd?.ads?.pageTargets?.usps) {
				imaParams.us_privacy = webmd.ads.pageTargets.usps;
			}

			// convert pos to string and add to videoParams
			videoParams.pos = posArr.toString();

			debug('[vueVideoPlayer] posArr', posArr);
			debug('[vueVideoPlayer] posSizes', posSizes);
			debug('[vueVideoPlayer] videoParams pos', videoParams.pos);

			/*
			 * merge video params into default pageTargets
			 * pass them to preroll ad call
			 */
			combinedParams = Object.assign(webmd.ads.pageTargets, videoParams);
			imaParams.cust_params = Object.assign(imaParams.cust_params, combinedParams);

			debug('[vueVideoPlayer] imaParams ciu_szs', imaParams.ciu_szs);

			// fire custom event
			this.emitter.emit('vp-params');
		},

		/**
		 * convertToCompanions
		 * Converts exisiting display slots to companion ads for preroll call,
		 * update correlator, and enable video ads Refresh.
		 */
		convertToCompanions() {
			debug('[vueVideoPlayer] convertToCompanions - update Correlator');

			// Defer Google Ad execution and push a command to execute it
			webmd.ads.googleDefer(() => {
				window.googletag.cmd.push(() => {
					window.googletag.pubads().getSlots().forEach((slot) => {
						slot.addService(window.googletag.companionAds());
					});

					/**
					 * Update the correlator for ad targeting
					 * Changes the correlator that is sent with ad requests.
					 */
					window.googletag.pubads().updateCorrelator();

					/**
					 * @setRefreshUnfilledSlots - Sets whether companion slots
					 * that have not been filled will be automatically backfilled.
					 */

					if (this.getQueryParam('refreshSlots') !== 'false') {
						setTimeout(() => {
							window.googletag.companionAds().setRefreshUnfilledSlots(false);
						}, 1200);
					}

					// Signals to GPT that video ads will be present on the page.
					window.googletag.pubads().enableVideoAds();

					// Enables all GPT services that have been defined for ad slots on the page.
					window.googletag.enableServices();

					/**
					 * PPE-533515 - Remove Delay
					 * Refresh in-view ads to ensure synchronized ad display
					 */
					this.refreshInviewAds();
				});
			});
		},

		/**
		 * setPageTargets
		 * set video-related page targets for ad call
		 */
		setPageTargets() {
			debug('[vueVideoPlayer] setPageTargets');

			webmd.ads.setPageTarget('vpt', this.video.topicId);

			// Set autply parameter in Display and Video ad calls
			if (this.config.autoplay) {
				webmd.ads.setPageTarget('autply', 1);
			} else {
				webmd.ads.setPageTarget('autply', 0);
			}
		},

		/**
		 * getAdNetworkCode
		 * if available, update network code with webmd utility
		 * otherwise use default, production network code
		 */
		getAdNetworkCode() {
			let networkCode = '';

			if (
				window.webmd
				&& window.webmd.ads
				&& window.webmd.ads.networkCode
			) {
				networkCode = window.webmd.ads.networkCode;
			} else {
				networkCode = '4312434';
			}

			this.adConfig.networkCode = networkCode;
		},

		/**
		 * makePlayerSticky
		 * Make the video player sticky when it goes out of view
		 */
		makePlayerSticky(event) {
			const html = document.querySelector('html');
			const videoContainer = document.getElementById(this.video.container);
			const videowrapper = videoContainer.getElementsByClassName('vue-video-wrapper')[0];
			const stickyHeaderBar = videoContainer.getElementsByClassName('sticky-header-bar')[0];
			const sideFlyout = videoContainer.getElementsByClassName('sticky-side-flyout')[0];
			const closeButton = stickyHeaderBar.getElementsByClassName('sticky-close-button')[0];
			const articleBody = videoContainer.closest('article')?.querySelector('.article__body');
			const $vm = this;
			let videoAutoPause = false;

			debug('[vueVideoPlayer] makePlayerSticky: enabled');
			this.showCountdownTimer = false;

			// Remove any existing scroll event listener
			if (this.scrollHandler) {
				window.removeEventListener('scroll', this.scrollHandler);
			}

			const handleScroll = () => {
				if ($vm.config.disableStickyPlayer === false && articleBody) {
					const videoViewable = window.akamai.amp.Utils.getViewability(videoContainer);

					if (videoViewable === 'none' && (articleBody.getBoundingClientRect().bottom > window.innerHeight)) {
						if (html.classList.contains('sticky')) {
							return;
						}

						if (window.innerWidth < 768) {
							html.classList.add('sticky', 'mobile-sticky');
							videowrapper.classList.add('sticky', 'mobile-sticky');
							stickyHeaderBar.style.display = 'block';
							sideFlyout.classList.add('show');
							closeButton.classList.add('mobile-button', $vm.closePos);
							videoContainer.classList.add('placeholder');
						} else {
							html.classList.add('sticky');
							videowrapper.classList.add('sticky');
							stickyHeaderBar.classList.add('show');
							videoContainer.classList.add('placeholder');
						}

						if (videoAutoPause) {
							event.player.play();
							videoAutoPause = false;
						}
					} else {
						if (!html.classList.contains('sticky')) {
							return;
						}

						if (window.innerWidth < 768) {
							html.classList.remove('sticky', 'mobile-sticky');
							videowrapper.classList.remove('sticky', 'mobile-sticky');
							stickyHeaderBar.style.display = 'none';
							sideFlyout.classList.remove('show');
							videoContainer.classList.remove('placeholder');
						} else {
							html.classList.remove('sticky');
							videowrapper.classList.remove('sticky');
							stickyHeaderBar.classList.remove('show');
							videoContainer.classList.remove('placeholder');
						}

						if (event.player.playState === 'playing' && videoViewable === 'none') {
							event.player.pause();
							videoAutoPause = true;
						}
					}
				}
			};

			// Store the scroll handler in the instance for later removal
			this.scrollHandler = debounce(handleScroll, 125);

			// eslint-disable-next-line func-names, prefer-arrow-callback
			window.addEventListener('scroll', this.scrollHandler, { passive: true });
			handleScroll();
			
			this.closeStickyPlayer(event);
		},

		/**
		 * closeStickyPlayer
		 * Close sticky player by clicking on close icon
		 */
		closeStickyPlayer(event) {
			const html = document.querySelector('html');
			const videoContainer = document.getElementById(this.video.container);
			const videowrapper = videoContainer.getElementsByClassName('vue-video-wrapper')[0];
			const stickyHeaderBar = videoContainer.getElementsByClassName('sticky-header-bar')[0];
			const sideFlyout = videoContainer.getElementsByClassName('sticky-side-flyout')[0];
			const stickyCloseButton = videoContainer.getElementsByClassName('sticky-close-button')[0];
			const stickyPlayer = event.player;

			stickyCloseButton.addEventListener('click', () => {
				if (videowrapper.classList.contains('sticky')) {
					html.classList.remove('sticky', 'mobile-sticky');
					videowrapper.classList.remove('sticky', 'mobile-sticky');
					stickyHeaderBar.classList.remove('show');
					videoContainer.classList.remove('placeholder');
					stickyHeaderBar.style.display = 'none';
					sideFlyout.classList.remove('show');
					this.config.disableStickyPlayer = true;
					stickyPlayer.pause();
					this.callAdhoc('stkcl', '-ctl');
				}
			});
		},

		/*
		 * Fetches videos based on the given topic ID or channel from SOLR
		 */
		getVideoQueue() {
			const $vm = this;
			const headers = oauth.generate({
				client_id: 'e4e3f73a-0ceb-4d37-939e-90ddb1238360',
				secret_key: '8720f366-3769-47e9-8156-187b9c6583b5',
			});
			const dataType = 'json';
			const apiUrl = videoQueueApi();
			const maxCount = 20;
			const q = '';
			const count = '';
			const excludeID = '';
			let filter = '';
			const topicId = this.pageData?.topic;

			switch ($vm.config.cpOptions.filter) {
				case 'topic':
					if ($vm.config.cpOptions.source === 'page') {
						filter += `primary_topic_id:${topicId}`;
					} else if ($vm.config.cpOptions.source === 'video') {
						filter += `primary_topic_id:${topicId}`;
					} else {
						debug('[vueVideoPlayer] - [Continuous Play] No topic id found');
					}
					break;

				case 'channel':
				default:
					if ($vm.config.cpOptions.source === 'page' && window.center_id) {
						filter += `channel_id:${window.center_id}`;
					} else {
						debug('[vueVideoPlayer] - [Continuous Play] No channel id found');
					}
					break;
			}
			axios
				.get(`${apiUrl}${q}${excludeID}${count}`, {
					headers,
					dataType,
					params: {
						q: filter,
						excludeID: $vm.chronicId,
						count: maxCount,
					},
				})
				.then((response) => {
					if (response.data !== '' && response.data !== null) {
						if (response.data === false) {
							$vm.createBeaconCall('playback-error', 'getVideoQueue', 'response-no-data');
							debug('[vueVideoPlayer]', 'Invalid VideoQueue');
						} else if (response.data.code > 200) {
							$vm.createBeaconCall('playback-error', 'getVideoQueue', 'response-unavailable');
							debug('[vueVideoPlayer] Video Queue Issue:', response.data);
						} else {
							const videoQueue = response.data;

							/*
							 * If video queue response length is greater than the max count,
							 * then get the first 8 elements from the response and update videoQueue
							 */
							/* eslint-disable no-unused-expressions */
							videoQueue.length > $vm.config.cpOptions.max
								? videoQueue.splice($vm.config.cpOptions.max, videoQueue.length)
								: videoQueue;
							/* eslint-disable no-unused-expressions */

							window.videoQueue = videoQueue;
							$vm.config.continuousPlay = 1;
							this.emitter.emit('vp-vqready');
							debug('[vueVideoPlayer] Video Queue Data:', response.data, videoQueue);
						}
					}
				})
				.catch((error) => {
					$vm.createBeaconCall('playback-error', 'getVideoQueue', 'failed-api-response');
					debug(error);
				});
		},

		/*
		 * Initialize Continuous play when the page loads
		 */
		initializeContinuousPlay() {
			this.getVideoQueue();

			/*
			 * if the first video is playing and if continuous play is true
			 * when video completes to 75% then showUpNext
			 */
			if (this.config.cpOptions.flyout) {
				this.emitter.on('vp-75pct', () => {
					if (window.videoQueue.length > 0) {
						this.showUpNext();
					}
				});
			}
		},

		getVideoFromEvent() {
			const $vm = this;

			document.addEventListener('evt-video-play', (e) => {
				debug('[vueVideoPlayer] requested:', e.detail);

				/*
				 * Branded video needs to differentiate between multiple videos
				 * Only play video in the container associated with playlist
				 */
				if ($vm.mode === 'branded') {
					const { vidId } = e.detail;
					const playlistChron = e.detail.chronicle;

					if (playlistChron === $vm.chronicId) {
						$vm.rebuildPlayer(vidId);
					}
				} else {
					$vm.rebuildPlayer(e.detail);
				}
			});
		},

		/*
		 * createInitialPlayer
		 * Things we must do the first time we create the player
		 */
		createInitialPlayer() {
			// if player has not been disabled
			if (!this.config.disablePlayer) {
				this.createVideoPlayer();

				/**
				 * this block should execute only once when the value for continuousPlay is boolean
				 * after the first video in the list plays, the value is changed to a number and continuousPlay is used as a counter
				 *
				 */
				if (this.config.continuousPlay === true) {
					this.initializeContinuousPlay();
				}
			} else {
				if (!this.config.checkRendition) {
					// when player is disabled, remove min-height and throw beacon error call
					document.querySelector('.vue-video-container').style.minHeight = 0;
					this.createBeaconCall('api-error', 'getVideoFromChron', 'failed-api-response');
				}

				debug('[vueVideoPlayer] player is disabled');
			}
		},

		resetPlayer() {
			// Reset all metric tracking variables
			this.triggeredMilestones = new Set();
			this.lastProgress = 0;

			// Remove all metric-related event listeners
			['vp-25pct', 'vp-50pct', 'vp-75pct'].forEach((event) => {
				this.emitter.off(event);
			});
			debug('[vueVideoPlayer] Reset player state and event listeners');
			this.noTranscriptAvailable = false;
		},

		/*
		 * rebuildPlayer
		 * Rebuild player for continuous play videos with enhanced cleanup
		 */
		rebuildPlayer(vidId) {
			const $vm = this;

			// Clean up old player instance
			if (this.ampPlayer) {
				this.ampPlayer.destroy();
				debug('[vueVideoPlayer] Old player instance destroyed');
			}

			// Reset all player state and event listeners
			this.resetPlayer();

			// Generate new unique IDs
			const randomNumb = Math.floor(Math.random() * 900) + 100;
			this.video.vidChron = vidId;
			this.video.container = `video-container-${vidId}-${randomNumb}`;
			this.video.id = `v_video_${vidId}-${randomNumb}`;

			debug('[vueVideoPlayer] Rebuilding new Video Player');

			// Initialize new player
			this.getVideoFromChron(this.video.vidChron);
			this.video.vidnum += 1;

			if (this.config.continuousPlay) {
				this.config.continuousPlay += 1;
			}

			// Setup flyout if needed
			if (
				this.config.cpOptions.flyout
				&& this.config.continuousPlay !== window.videoQueue.length + 1
			) {
				this.createUpNextFlyout();
				this.emitter.on('vp-75pct', () => {
					this.showUpNext();
				});
			}

			// Setup sticky behavior if needed
			if (this.config.continuousPlay) {
				$vm.emitter.on('vp-ready', () => {
					setTimeout(() => {
						this.stickyUpNext();
					}, 200);
				});
			}
		},

		/*
		 * createUpNextFlyout
		 * Gets the next video data from the video queue
		 * Saves chronId, title, thumbnail and alt text to the upNext data
		 */
		createUpNextFlyout() {
			const videos = window.videoQueue;
			const nextVideo = videos[this.config.continuousPlay - 1];

			this.upNext.chronId = nextVideo.chronId;
			this.upNext.title = nextVideo.title;

			if (nextVideo.thumbnail) {
				this.upNext.thumb = this.config.image_server_url + nextVideo.thumbnail.src;
				this.upNext.alt = nextVideo.thumbnail.alt;
			} else {
				this.upNext.thumb = `${this.config.image_server_url}/webmd/consumer_assets/site_images/article_thumbnails/video/default-thumbnail.png`;
				this.upNext.alt = 'Next Video';
			}

			debug('[vueVideoPlayer] Up Next', this.upNext.title);
		},

		/*
		 * showUpNext function
		 * Display the UpNext Flyout contents in an overlay over the video
		 */
		showUpNext() {
			const upNextContainer = document.getElementById(this.video.container).getElementsByClassName('upnext-flyout-container')[0];
			const upNextlink = document.getElementById(this.video.container).getElementsByClassName('upnext-flyout-contents')[0];
			const upNextOpen = upNextContainer.getElementsByClassName('webmd-icon-left-arrow')[0];
			const upNextClose = upNextContainer.getElementsByClassName('webmd-icon-right-arrow')[0];

			upNextContainer.classList.add('show');

			// Title/Thumbnail click handler: trigger the next video
			upNextlink.addEventListener('click', () => {
				this.callAdhoc('upnext', '-adv');

				/*
				 * this.video.vidChron = this.upNext.chronId;
				 * this.video.container = `video-container-${this.upNext.chronId}`;
				 * this.video.id = `v_video_${this.upNext.chronId}`;
				 */
				this.rebuildPlayer(this.upNext.chronId);
			});

			upNextOpen.addEventListener('click', () => {
				this.callAdhoc('upnext', '-op');
			});

			upNextClose.addEventListener('click', () => {
				this.callAdhoc('upnext', '-cl');
			});
		},

		/*
		 * stickyUpNext
		 * Show sticky player when scrolled to the bottom and clicked on upNext
		 */
		stickyUpNext() {
			const $vm = this;
			const html = document.querySelector('html');
			const videoContainer = document.getElementById(this.video.container);
			const videowrapper = videoContainer.getElementsByClassName('vue-video-wrapper')[0];
			const stickyHeaderBar = videoContainer.getElementsByClassName('sticky-header-bar')[0];
			const sideFlyout = videoContainer.getElementsByClassName('sticky-side-flyout')[0];
			const disableSticky = $vm.config.disableStickyPlayer;
			const videoViewable = window.akamai.amp.Utils.getViewability(videoContainer);
			const videowrapperHeight = videowrapper.offsetHeight;
			const closeButton = stickyHeaderBar.getElementsByClassName('sticky-close-button')[0];
			const articleBotttom = document.getElementsByClassName('article__body')[0];
			const videowrapperParent = document.getElementsByClassName('video-wrapper')[0];

			if (disableSticky === false) {
				if (window.innerWidth < 768) {
					if (videowrapperParent.getBoundingClientRect().bottom <= videowrapperHeight / 2 && articleBotttom.getBoundingClientRect().bottom > videowrapperHeight) {
						html.classList.add('sticky', 'mobile-sticky');
						videowrapper.classList.add('sticky', 'mobile-sticky');
						stickyHeaderBar.style.display = 'block';
						sideFlyout.classList.add('show');
						closeButton.classList.add('mobile-button', this.closePos);
						videoContainer.classList.add('placeholder');
					}
				} else if (videoViewable === 'none') {
					html.classList.add('sticky');
					videowrapper.classList.add('sticky');
					stickyHeaderBar.classList.add('show');
					videoContainer.classList.add('placeholder');
				}
			}
		},

		/*
		 * enableInfoControl
		 * Enable infobar and control bar after pre-roll ad ends
		 */
		enableInfoControl() {
			const videoContainer = document.getElementById(this.video.container);
			const infoBar = videoContainer.getElementsByClassName('infobar-container')[0];
			const progressBar = videoContainer.getElementsByClassName('amp-progress')[0];

			if (infoBar && progressBar) {
				infoBar.style.display = 'block';
				progressBar.style.display = 'block';
			}
		},

		showUnmuteOverlay() {
			const videoContainer = document.getElementById(this.video.container);
			const unmute = videoContainer.getElementsByClassName('amp-unmute')[0];
			const unmuteSpan = document.createElement('span');
			const muteText = document.createTextNode('Click to Unmute');

			unmuteSpan.appendChild(muteText);
			unmute.appendChild(unmuteSpan);

			// Remove the unmute overlay text after five seconds
			setTimeout(() => {
				unmute.removeChild(unmuteSpan);
			}, 5000);
		},

		updateVideoContainer() {
			const randomNumb = Math.floor(Math.random() * 900) + 100;
			this.video.container = `video-container-${this.video.vidChron}-${randomNumb}`;
			this.video.id = `v_video_${this.video.vidChron}-${randomNumb}`;
		},

		updateVolBarLayout() {
			if (this.mode === 'interactive') {
				const iaVideo = document.querySelectorAll('.ia-video');

				iaVideo.forEach((item) => {
					if (item.classList.contains('left') || item.classList.contains('right')) {
						this.config.plugins.react.volume = 'horizontal';
					}
				});
			}
		},

		multiplayerPlayPause(click) {
			const $vm = this;

			// Execute when user clicks video to play
			if (click === 'play') {
				if ($vm.ampPlayer.appState.playState !== 'playing') {
					$vm.isUserPaused = false;
					if ($vm.config.dynVidId !== '' && $vm.config.inviewAutoplay) {
						window.dynVideoState[$vm.config.dynVidId].userState = 'play';

						window.isInviewMap.forEach((value, key, map) => {
							if ($vm.config.dynVidId !== key) {
								if (window.vueVideo[key]._player.playState === 'playing') {
									window.vueVideo[key].pause();
								}

								/*
								 * need to change user state for all other videos
								 * because now one video was manually clicked to play
								 */
								if (window.dynVideoState[key].userState !== 'autoplay') {
									window.dynVideoState[key].userState = 'pause';
								}
							}
						});
					}
				}
			} else if (click === 'pause') {
			// Execute when user clicks video to pause
				if ($vm.ampPlayer.appState.playState === 'playing') {
					$vm.isUserPaused = true;
					if ($vm.config.dynVidId !== '' && $vm.config.inviewAutoplay) {
						window.dynVideoState[$vm.config.dynVidId].userState = 'pause';

						// Set nowplaying empty when nowplaying video is paused manually
						if (window.nowPlaying === $vm.config.dynVidId) {
							window.nowPlaying = '';
						}
					}
				}
			}
		},

		// PPE-384143: get the Amazon A9 bids
		getA9Bids() {
			try {
				const $vm = this;
				const a9Timeout = 800;
				const slots = [];
				let custParams = $vm.config.plugins.ima.adTagUrl.params.cust_params;

				if (typeof window.apstag === 'undefined') {
					debug('===== APSTAG is undefined =====');
					return;
				}

				// Remove the existing bid values from cust params
				Object.keys(custParams).forEach((key) => {
					if (key.includes('amzn')) {
						delete custParams[key];
					}
				});

				slots.push({
					slotID: 'videoSlot',
					mediaType: 'video',
					sizes: [[640, 360]],
				});

				if (slots.length > 0) {
					// if there aren't any slots to bid on
					window.apstag.fetchBids({
						slots,
						timeout: a9Timeout,
					}, (bids) => {
						const bidRes = {};

						if (bids.length > 0) {
							bids.forEach((bid) => {
								if (bid.mediaType === 'video') {
									Object.keys(bid.targeting).forEach((key) => {
										if (typeof key !== 'undefined' && typeof bid.targeting[key] !== 'undefined') {
											bidRes[key] = bid.targeting[key];
										}
									});
								}
							});

							custParams = Object.assign(custParams, bidRes);
						}
					});
				}
			} catch (e) {
				debug(e);
			}
		},
	},

	computed: {

		/**
		 * getPageData
		 * Gets pagedata from and returns page properites
		 */
		getPageData() {
			const { ad, omniture: omni } = this.store.pagedata;
			let pageName = omni.s_pagename;
			const { mode } = this;

			// Change Page Name
			switch (mode) {
				case 'fed':
				case 'branded':
					pageName = pageName.replace('/video/', '/vidrprem/');
					break;
				case 'daily':
					pageName = pageName.replace('/video/', '/vidsite/');
					break;
				default:
					break;
			}

			return {
				topic: omni.s_topic,
				leaf: ad?.targets.leaf,
				pageId: omni.s_asset_id,
				uri: ad?.targets.uri,
				pageName,
				packageName: omni.s_package_name,
			};
		},

		getImageBaseUrl() {
			return this.store.pagedata?.omniture?.image_server_url;
		},

		timerOverlay() {
			const minutes = Math.floor(this.remainingTime / 60).toString().padStart(2, '0');
			const seconds = (this.remainingTime % 60).toString().padStart(2, '0');
			return `${minutes}:${seconds}`;
		},
	},

	created() {
		this.store = useCmsStore(this.$pinia);
		if (this.mode !== 'symphony') {
			this.pageData = this.getPageData;
			this.updatePageData();
		}
		this.config.image_server_url = this.getImageBaseUrl;
		this.sTestValue = this.store.pagedata?.omniture?.s_test === 'DURY';
	},

	beforeMount() {
		if (this.getQueryParam('video') !== 'false') {
			// set useragent
			this.uaType = ua.type || 'mobile';
			debug('[vueVideoPlayer] uaType:', this.uaType);

			this.ampScriptPromise = attachPlatformScript();
			this.applyModeConfig();
			this.mergeOptions();
			this.checkPrerollState();
			this.checkDisableInitialAd();
		} else if (document.getElementsByClassName('video-playlist-container')[0]) {
			document.getElementsByClassName('video-playlist-container')[0].style.display = 'none';
		}
		this.showCountdownTimer = this.sTestValue && !this.config.stickyPlayer;
	},

	mounted() {
		if (this.getQueryParam('video') !== 'false' && !this.config.disablePlayer) {
			// update custom events on vue instance
			this.vueCustomEvents();

			/**
			 * Run ads code first after modes and option configs are set
			 * This should give ads more time to execute before the player code is initialized
			 */
			this.updateIuPartsPreroll();

			// only set ad-related params if preroll is not disabled
			if (!this.config.disablePreroll) {
				this.setPageTargets();
			}
			this.checkForPlaylist();
			this.updateVolBarLayout();

			this.video.vidChron = this.chronicId;

			this.updateVideoContainer();

			// call video API to update the remaining config values
			this.getVideoFromChron(this.chronicId);

			const { vueVideo } = window;
			window.webMDBeaconCalls = window.webMDBeaconCalls || [];

			/**
			 * Create window objects for InView Autoplay functionality
			 * Global window objects are needed to understand the number and state of videos on a page
			 * Since each video is its own Vue instance, we need to use the window object
			 */
			if (this.config.inviewAutoplay) {
				window.dynVideoState = window.dynVideoState || {};
				window.dynVideoState[this.config.dynVidId] = {
					userState: 'autoplay',
					audioState: 'muted',
				};
				window.nowPlaying = window.nowPlaying || '';
				window.isInviewMap = window.isInviewMap || new Map();
				window.isInviewMap.set(this.config.dynVidId, false);

				/* Get Scroll Direction */
				window.addEventListener('scroll', () => {
					this.scrollDirection = window.scrollY > this.lastScrollY ? 'down' : 'up';
					this.lastScrollY = window.scrollY;
				}, { passive: true });
			}

			if (!webmd.ads.disableAdsInit && this.config.refreshAds && !this.config.disablePreroll) {
				const self = this;
				window.webmd.ads.googleDefer(() => {
					window.googletag.pubads().addEventListener('slotResponseReceived', (event) => {
						try {
							const slotElementId = event.slot.getSlotElementId();
							debug('[vueVideoPlayer]slotResponseReceived:', slotElementId);
							self.initObserver(slotElementId);
						} catch (error) {
							debug(error);
						}
					});
				});
			}
		}
		if (window.innerWidth < 768) {
			this.config.cpOptions.flyout = false;
		}
	},
};
</script>

<style lang="scss">
@import "scss/main";
</style>
